import React, { useCallback, useEffect, useState } from 'react';
import store from './journalStore';
import compressImage from './compressImage';
import PhotoImg from './PhotoImg';
import Slideshow from './Slideshow';

const pad = (n) => String(n).padStart(2, '0');
const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

export default function Gallery({ passcode }) {
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState('');
  const [error, setError] = useState('');
  const [uploadDate, setUploadDate] = useState(todayYmd());
  const [caption, setCaption] = useState('');
  const [show, setShow] = useState(null); // { index, autoplay }

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setPhotos(await store.photos(passcode));
    } catch (e) {
      setError('Could not load the album.');
    } finally {
      setLoading(false);
    }
  }, [passcode]);

  useEffect(() => {
    load();
  }, [load]);

  const onPick = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = '';
    if (!files.length) return;
    setError('');
    const me = localStorage.getItem('anusha_journal_me') || 'Us';
    let done = 0;
    for (const f of files) {
      done += 1;
      setUploading(`Adding ${done}/${files.length}…`);
      try {
        // eslint-disable-next-line no-await-in-loop
        const { base64 } = await compressImage(f);
        // eslint-disable-next-line no-await-in-loop
        await store.addPhoto(passcode, { date: uploadDate, caption: caption.trim(), author: me, base64 });
      } catch (err) {
        setError(err.message || 'Upload failed.');
      }
    }
    setUploading('');
    setCaption('');
    await load();
  };

  const openShow = (index, autoplay = false) => setShow({ index, autoplay });
  const closeShow = () => {
    setShow(null);
    load(); // reflect removals / date-moves done in the slideshow
  };

  return (
    <div className="aj-gallery-wrap">
      <div className="aj-gallery-top">
        <label className={`aj-btn aj-upload-btn${uploading ? ' is-busy' : ''}`}>
          {uploading || '➕ Add photos'}
          <input type="file" accept="image/*" multiple hidden onChange={onPick} disabled={!!uploading} />
        </label>
        <label className="aj-date-pick">
          <span>dated</span>
          <input
            type="date"
            className="aj-date-input"
            value={uploadDate}
            max={todayYmd()}
            onChange={(e) => setUploadDate(e.target.value)}
          />
        </label>
        <input
          type="text"
          className="aj-date-input aj-cap-input"
          value={caption}
          maxLength={140}
          onChange={(e) => setCaption(e.target.value)}
          placeholder="caption — say something cute 😏 (optional)"
        />
        {photos.length > 1 && (
          <button type="button" className="aj-btn-ghost" onClick={() => openShow(0, true)}>
            ▶ Slideshow
          </button>
        )}
      </div>
      {error && <div className="aj-err">{error}</div>}

      {loading ? (
        <div className="aj-muted">Loading our album…</div>
      ) : photos.length === 0 ? (
        <div className="aj-muted">No photos yet — add your first memory together. 📸💕</div>
      ) : (
        <div className="aj-photo-grid">
          {photos.map((p, i) => (
            <button
              type="button"
              className="aj-photo-cell"
              key={p.id}
              onClick={() => openShow(i, false)}
              aria-label="open photo"
            >
              <PhotoImg passcode={passcode} id={p.id} className="aj-photo-thumb" alt={p.caption || 'our memory'} />
            </button>
          ))}
        </div>
      )}

      {show && (
        <Slideshow
          passcode={passcode}
          photos={photos}
          startIndex={show.index}
          autoplay={show.autoplay}
          onClose={closeShow}
        />
      )}
    </div>
  );
}
